import { Component, ErrorInfo, ReactNode } from 'react';

import { FeaturesRequest } from '@/app/libs/sentry/types/features';
import { captureException } from '@/app/libs/sentry/utils/exception';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundaryProvider extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
  };

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errInfo = errorInfo.componentStack?.toString() || '';
    captureException(error, FeaturesRequest.SYSTEM, {
      extra: { componentStack: errorInfo.componentStack },
    });

    throw new Error(`An error ocurred. ERROR:00 #%d ${errInfo}. ${error.message}`);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8 text-center">
            <div>
              <h2 className="mt-6 text-3xl font-extrabold sm:text-4xl">Oops! Algo deu errado</h2>
              <p className="mt-2 text-sm text-gray-600 sm:text-base">
                Pedimos desculpas pelo inconveniente. Ocorreu um erro inesperado.
              </p>
            </div>
            <div className="mt-8">
              <button
                onClick={() => window.location.reload()}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundaryProvider };
